import * as React from 'react'
import { Box, Button } from '@mui/material'
import { INode } from 'shared/types/tree'
import { IQuestionIssueResolution } from './questions/QuestionCleanup'
import { getQuestionsRequiringManualFix } from './questions/QuestionEvaluation'
import { createIdMap, NodeIdMap } from '../../version_mapping/utility'
import {
  getQuestionTeachersNoteContent,
  getQuestionType,
} from '../../utility/TreeHelpers'
import { openNode } from '../../utility/TreeNavigation'
import { PotentiallyHtml } from '../../PotentiallyHtml'
import { PresentationsStudentFacing } from './presentations/PresentationsStudentFacing'
import { QuestionType } from 'shared/types/ingestion'
import { useNavigateTo, useParams } from 'infrastructure/routing'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import { allProductsById } from '../../../api/masters.atoms'
import { ProductSelection } from '../products/ProductSelection'
import { useGetTreeById } from '../../../reducers/trees_hooks'

export function IDVerificationWrapper() {
  return (
    <Box display="flex" flexDirection="row">
      <IDVerificationProductIndex />
      <WithTree />
    </Box>
  )
}

function IDVerificationProductIndex() {
  const { productId } = useParams()
  const navigate = useNavigateTo()
  const onProductSelect = ({ id }: { id: number }) => {
    navigate(`/id_verification/${id}`)
  }
  return (
    <Box width="250px">
      <ProductSelection
        onSelect={onProductSelect}
        selectedProductId={Number(productId)}
      />
    </Box>
  )
}

enum verificationViews {
  QuestionFixes,
  PresentationStudentFacing,
}

function WithTree() {
  const allProducts = useAtomValue(allProductsById)
  const { productId } = useParams()
  const product = (productId && allProducts[Number(productId)]) || undefined

  const ids = product?.editable_tree
  const { tree } = useGetTreeById({
    treeId: ids?.tree_id,
    versionId: ids?.version_id,
  })

  if (!product) return null
  if (!tree) return <Box>Loading...</Box>

  return (
    <Box>
      <Box>
        <b>{tree.title}</b>
      </Box>
      <IDVerification tree={tree} />
    </Box>
  )
}

function IDVerification({ tree }: { tree: INode }) {
  const [idMap, setIdMap] = React.useState<NodeIdMap>()
  const [currentView, setCurrentView] = React.useState(-1)

  React.useEffect(() => {
    setIdMap(createIdMap(tree))
  }, [tree])

  if (!idMap) return <Box>Loading...</Box>

  return (
    <Box>
      <Box>
        <Button
          disabled={currentView === verificationViews.QuestionFixes}
          onClick={() => setCurrentView(verificationViews.QuestionFixes)}
        >
          Resolve Questions
        </Button>
        <Button
          disabled={currentView === verificationViews.PresentationStudentFacing}
          onClick={() =>
            setCurrentView(verificationViews.PresentationStudentFacing)
          }
        >
          Resolve Presentation Student Facing
        </Button>
      </Box>
      <QuestionsToResolve
        tree={tree}
        idMap={idMap}
        hide={currentView !== verificationViews.QuestionFixes}
      />
      {currentView === verificationViews.PresentationStudentFacing && (
        <PresentationsStudentFacing tree={tree} />
      )}
    </Box>
  )
}

function QuestionsToResolve({
  tree,
  idMap,
  hide,
}: {
  tree: INode
  idMap: NodeIdMap
  hide: boolean
}) {
  const [withIssues, setWithIssues] = React.useState<{
    [qid: string]: IQuestionIssueResolution
  }>()

  React.useEffect(() => {
    setWithIssues(getQuestionsRequiringManualFix(tree))
  }, [tree])

  if (hide) {
    return null
  }

  if (!withIssues) return <Box>Loading...</Box>

  return (
    <Box>
      {Object.keys(withIssues).map((qid) => {
        return (
          <QuestionInfo
            key={qid}
            question={idMap[qid].node}
            idMap={idMap}
            issues={withIssues[qid]}
          />
        )
      })}
    </Box>
  )
}

function QuestionInfo({
  question,
  idMap,
  issues,
}: {
  question: INode
  idMap: NodeIdMap
  issues: IQuestionIssueResolution
}) {
  const [showMore, setShowMore] = React.useState(false)
  const [teachersNote, setTeachersNote] = React.useState('')
  React.useEffect(() => {
    setTeachersNote(getQuestionTeachersNoteContent(question) || '')
  }, [question])
  return (
    <Box>
      <Box>
        <Button onClick={() => openNode(question, idMap)}>
          {`${getQuestionType(question)}: ${question.title}`}
        </Button>
        <Button onClick={() => setShowMore(!showMore)}>More</Button>
      </Box>
      {showMore && !!question.description && (
        <Box padding={'10px'}>
          <Box padding={'5px'}>
            <Box>
              <b>Resolution:</b>
            </Box>
            {issues.descriptions.map((d, i) => (
              <Box key={i}>{d}</Box>
            ))}
          </Box>
          <Box
            padding={'5px'}
            maxWidth={'80%'}
            fontSize={12}
            style={{ backgroundColor: 'white' }}
            borderRadius={'5px'}
          >
            Question Content:
            <PotentiallyHtml s={question.description} />
          </Box>
          {getQuestionType(question) === QuestionType.ShortAnswer && (
            <Box>
              <Box>
                <b>Teachers Note</b>
              </Box>
              <Box
                padding={'5px'}
                maxWidth={'80%'}
                fontSize={12}
                style={{ backgroundColor: 'white' }}
                borderRadius={'5px'}
              >
                <PotentiallyHtml s={teachersNote} />
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}
